import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit2, Trash2, Briefcase, Search, Calendar, DollarSign, User, MoreVertical, Grid, List, SortAsc, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { FY } from '../lib/fiscal';
import { ProjectsAnalytics } from './ProjectsAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeStatuses, setActiveStatuses] = useState([]); // multi-select status toggles
  const [projectManagerFilter, setProjectManagerFilter] = useState('all');
  const currentYearPrefix = FY.short; // fiscal-year prefix, e.g. '27' from Nov 1 2026
  const prevYearPrefix = String(Number(FY.short) - 1).padStart(2, '0');
  // First 90 days of a new fiscal year: show last year's jobs too (they're still closing out)
  const [yearFilter, setYearFilter] = useState(() => (Date.now() - FY.start.getTime() < 90 * 864e5 ? 'recent' : currentYearPrefix));

  // Set default PM filter to logged-in user's name on first load
  useEffect(() => {
    if (user && projects.length > 0 && projectManagerFilter === 'all') {
      const myPMName = getUserPMName();
      // Default to the logged-in PM's projects (any role) if they have any
      if (myPMName && projects.some(p => p.assigned_manager === myPMName)) {
        setProjectManagerFilter(myPMName);
      }
    }
  }, [user, projects.length]);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [sortBy, setSortBy] = useState('project_number');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('projectsViewMode');
    // Force list view as default, even if tiles was previously saved
    if (!saved || saved === 'tiles') {
      localStorage.setItem('projectsViewMode', 'list');
      return 'list';
    }
    return saved;
  });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('new') === '1') { setIsCreateOpen(true); navigate('/projects', { replace: true }); }
    if (sp.get('q')) { setSearchTerm(sp.get('q')); setYearFilter('all'); navigate('/projects', { replace: true }); }
  }, [location.search]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    description: '',
    quote_id: '',
    start_date: '',
    end_date: '',
    estimated_budget: '',
    assigned_manager: '',
    contact_person: '',
    customer_po_number: '',
    // Site Information for SMS Dispatch System
    site_name: '',
    site_address: '',
    site_contact_name: '',
    site_contact_phone: '',
    site_contact_email: ''
  });

  useEffect(() => {
    fetchProjects();
    fetchQuotes();
  }, []);

  // Persist view mode preference
  useEffect(() => {
    localStorage.setItem('projectsViewMode', viewMode);
  }, [viewMode]);

  // Get unique project managers and customers for filter dropdowns
  // Filter out null, undefined, empty strings, and removed PMs
  const VISIBLE_PMS = ['Jeff H', 'Justin K', 'Jeff D', 'Carl L', 'Rick W'];
  const hiddenPMs = [...new Set(projects.map(p => p.assigned_manager).filter(m => m && m.trim() !== '' && !VISIBLE_PMS.includes(m)))];
  const uniqueProjectManagers = VISIBLE_PMS.filter(m => projects.some(p => p.assigned_manager === m));
  const uniqueCustomers = [...new Set(projects.map(p => p.client_name).filter(c => c && c.trim() !== ''))].sort();
  // Available project-number years (prefix), newest first — e.g. ['26','25','24']
  const availableYears = [...new Set(projects.map(p => (p.project_number || '').split('-')[0]).filter(y => /^\d{2}$/.test(y)))].sort().reverse();

  // Check if user can see advanced filters (Admin/Super Admin roles)
  const canUseAdvancedFilters = user && ['admin', 'super_admin'].includes(user.role);

  // Normalize PM names to avoid duplicates and clean up formatting
  const normalizePMName = (name) => {
    if (!name) return name;
    
    // Remove trailing numbers like "-6"
    name = name.replace(/-\d+$/, '').trim();
    
    // Filter out junk entries
    const junkNames = ['xxxxxxxxx', 'xxxxxxxx', 'CG', 'cg'];
    if (junkNames.includes(name) || junkNames.includes(name.toLowerCase())) return null;
    
    // Normalize specific names
    const nameMap = {
      'Matt Strohmann': 'Matt S',
      'Matt s': 'Matt S',
      'matt s': 'Matt S',
      'matt strohmann': 'Matt S',
      'Jeff Henhoeffer': 'Jeff H',
      'Jeff h': 'Jeff H',
      'jeff h': 'Jeff H',
      'jeff henhoeffer': 'Jeff H',
      'Cody Wilson': 'Cody W',
      'cody wilson': 'Cody W',
      'Craig G-6': 'Craig G',
      'Craig G -6': 'Craig G',
      'craig g-6': 'Craig G',
      'craig g': 'Craig G',
      'Craig G': 'Craig G'
    };
    
    // Check for exact match first
    const normalized = nameMap[name];
    if (normalized) return normalized;
    
    // Check case-insensitive match
    const lowerName = name.toLowerCase();
    for (const [key, value] of Object.entries(nameMap)) {
      if (key.toLowerCase() === lowerName) {
        return value;
      }
    }
    
    return name;
  };

  // Map logged-in user's full name to the PM name used in projects
  const getUserPMName = () => {
    if (!user?.full_name) return null;
    const name = user.full_name;
    const pmMap = {
      'Justin Koleszar': 'Justin K',
      'Jeff Henhoeffer': 'Jeff H',
      'Matt Strohmann': 'Matt S',
      'Carl Laput': 'Carl L',
      'Jeff Dickson': 'Jeff D',
      'Cody Wilson': 'Cody W',
      'Craig Gorham': 'Craig G',
      'Rick Wolf': 'Rick W',
    };
    return pmMap[name] || normalizePMName(name) || name;
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // Normalize PM names in the fetched projects and filter out junk
      const normalizedProjects = response.data
        .map(project => ({
          ...project,
          assigned_manager: normalizePMName(project.assigned_manager)
        }))
        .filter(project => project.assigned_manager !== null);
      setProjects(normalizedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quotes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    const toastId = toast.loading('Pulling latest jobs from the Work Orders sheet…');
    try {
      const token = localStorage.getItem('token');
      try {
        const syncRes = await axios.post(`${API}/projects/refresh-from-sheets`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (syncRes.data?.status === 'error') {
          toast.error(`Sheet sync problem: ${syncRes.data.message || 'unknown error'}`, { duration: 10000 });
        } else if (syncRes.data?.status === 'partial') {
          toast.warning(`Google didn't return one of the Work Orders books (${syncRes.data.message}). Nothing was removed — try Refresh again in a minute.`, { duration: 12000 });
        } else if (syncRes.data?.synced) {
          toast.success(`Pulled ${syncRes.data.synced} work-order lines from the sheets`);
        }
      } catch (syncErr) {
        if (syncErr.response?.status !== 403) {
          toast.error('Could not pull from the Work Orders sheet — showing saved data', { duration: 8000 });
        }
      }
      await fetchProjects();
      await fetchQuotes();
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh projects');
    } finally {
      toast.dismiss(toastId);
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        assigned_manager: normalizePMName(formData.assigned_manager),
        estimated_budget: parseFloat(formData.estimated_budget) || 0,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        quote_id: formData.quote_id && formData.quote_id !== 'none' ? formData.quote_id : null
      };

      const created = await axios.post(`${API}/projects`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(`Work order ${created.data?.project_number || ''} created — row written to ${submitData.assigned_manager}'s tab`);
      setIsCreateOpen(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        assigned_manager: normalizePMName(formData.assigned_manager),
        estimated_budget: parseFloat(formData.estimated_budget),
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        quote_id: formData.quote_id || null
      };

      await axios.put(`${API}/projects/${selectedProject.id}`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Project updated successfully');
      setIsEditOpen(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  const handleDeleteProject = (projectId, projectTitle) => {
    console.log('🗑️ Delete initiated for:', projectId, projectTitle);
    setProjectToDelete({ id: projectId, title: projectTitle });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    
    try {
      console.log('✓ User confirmed - proceeding with deletion...');
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ No auth token found');
        toast.error('Authentication required. Please log in again.');
        setDeleteConfirmOpen(false);
        setProjectToDelete(null);
        return;
      }
      
      console.log('🔄 Sending DELETE request...');
      
      const response = await axios.delete(`${API}/projects/${projectToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Delete successful:', response.data);
      toast.success('✅ Project deleted successfully');
      
      // Close dialog and reset
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
      
      // Refresh the project list
      await fetchProjects();
      
    } catch (error) {
      console.error('❌ Delete failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete project';
      toast.error(`❌ ${errorMessage}`);
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
    }
  };

  const cancelDelete = () => {
    console.log('❌ User cancelled deletion');
    setDeleteConfirmOpen(false);
    setProjectToDelete(null);
  };

  const handleToggleWIP = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/projects/${projectId}/toggle-wip`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(`Project ${response.data.is_wip ? 'marked as WIP' : 'marked as completed'}`);
      fetchProjects();
    } catch (error) {
      console.error('Error toggling WIP status:', error);
      toast.error('Failed to update WIP status');
    }
  };

  const handleStatusUpdate = async (projectId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/projects/${projectId}`, { status: newStatus }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Project status updated');
      fetchProjects();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      client_name: '',
      description: '',
      quote_id: '',
      start_date: '',
      end_date: '',
      estimated_budget: '',
      assigned_manager: '',
      // Site Information for SMS Dispatch System
      site_name: '',
      site_address: '',
      site_contact_name: '',
      site_contact_phone: '',
      site_contact_email: ''
    });
    setSelectedProject(null);
  };

  const startEdit = (project) => {
    setSelectedProject(project);
    setFormData({
      title: project.title,
      client_name: project.client_name,
      description: project.description,
      quote_id: project.quote_id || '',
      start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
      end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : '',
      estimated_budget: project.estimated_budget.toString(),
      assigned_manager: project.assigned_manager,
      // Site Information for SMS Dispatch System
      site_name: project.site_name || '',
      site_address: project.site_address || '',
      site_contact_name: project.site_contact_name || '',
      site_contact_phone: project.site_contact_phone || '',
      site_contact_email: project.site_contact_email || ''
    });
    setIsEditOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      planning: 'status-planning',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      on_hold: 'status-on-hold',
      cancelled: 'status-cancelled'
    };
    return `status-badge ${statusClasses[status] || 'status-planning'}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-purple-600',
      in_progress: 'bg-orange-600',
      completed: 'bg-green-600',
      on_hold: 'bg-yellow-600',
      cancelled: 'bg-red-600'
    };
    return colors[status] || 'bg-purple-600';
  };

  const filteredProjects = (() => {
    let filtered = projects;

    // Only the current PMs are visible (older PMs' jobs stay in the data, just not listed)
    if (!hiddenPMs.includes(projectManagerFilter)) {
      filtered = filtered.filter(p => !hiddenPMs.includes(p.assigned_manager));
    }

    // Year filter (by project number prefix, e.g. 27-xxxx); 'recent' = this fiscal year + last
    if (yearFilter === 'recent') {
      filtered = filtered.filter(p => [currentYearPrefix, prevYearPrefix].includes((p.project_number || '').split('-')[0]));
    } else if (yearFilter !== 'all') {
      filtered = filtered.filter(p => (p.project_number || '').split('-')[0] === yearFilter);
    }

    // Role-based filtering: PM/Estimators see only their projects UNLESS they're specifically filtering by another PM
    if (user && user.role === 'project_manager_estimator' && projectManagerFilter === 'all') {
      filtered = filtered.filter(project => project.assigned_manager === user.full_name);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(project => 
        (project.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.project_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.assigned_manager || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    // Status toggles (In Progress / Sent / Paid) — multi-select; empty = show all
    if (activeStatuses.length > 0) {
      filtered = filtered.filter(project => activeStatuses.includes(project.status));
    }

    // Apply project manager filter (available to all users)
    if (projectManagerFilter !== 'all') {
      filtered = filtered.filter(project => project.assigned_manager === projectManagerFilter);
    }

    // Apply advanced filters (only for admin/super admin)
    if (canUseAdvancedFilters) {
      if (customerFilter !== 'all') {
        filtered = filtered.filter(project => project.client_name === customerFilter);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'project_manager':
          aValue = a.assigned_manager || '';
          bValue = b.assigned_manager || '';
          break;
        case 'customer':
          aValue = a.client_name || '';
          bValue = b.client_name || '';
          break;
        case 'pm_customer':
          aValue = `${a.assigned_manager || ''}_${a.client_name || ''}`;
          bValue = `${b.assigned_manager || ''}_${b.client_name || ''}`;
          break;
        case 'project_number':
          aValue = a.project_number || '';
          bValue = b.project_number || '';
          break;
        case 'title':
          aValue = a.title || '';
          bValue = b.title || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'estimated_budget':
          aValue = a.estimated_budget || 0;
          bValue = b.estimated_budget || 0;
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  })();

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Projects</h1>
            <p className="text-slate-600">Manage your active and completed projects</p>
          </div>
          <div className="skeleton h-10 w-32"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="skeleton h-4 w-full mb-2"></div>
                <div className="skeleton h-4 w-2/3 mb-4"></div>
                <div className="skeleton h-8 w-20"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in bg-gradient-to-br from-slate-100 via-slate-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-8 bg-white/80 backdrop-blur-sm p-4 sm:p-6 shadow-sm border-b border-slate-200">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Projects</h1>
          <p className="text-xs sm:text-sm text-slate-600">
            {filteredProjects.length} projects
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleRefresh}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm"
            size="sm"
          >
            <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                New Work Order
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white/60 backdrop-blur-xl border-slate-200 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-slate-900">New Work Order</DialogTitle>
              <DialogDescription className="text-slate-600">
                The next number is claimed from the PM's tab in the <b>Work Orders {FY.long}</b> sheet and the row is written there on save.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title" className="text-slate-800">Project Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="client_name" className="text-slate-800">Client Name</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="estimated_budget" className="text-slate-800">Estimated Budget (optional)</Label>
                  <Input
                    id="estimated_budget"
                    type="number"
                    step="0.01"
                    value={formData.estimated_budget}
                    onChange={(e) => setFormData({...formData, estimated_budget: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                <div>
                  <Label htmlFor="assigned_manager" className="text-slate-800">Project Manager (whose tab / series)</Label>
                  <select
                    id="assigned_manager"
                    value={formData.assigned_manager}
                    onChange={(e) => setFormData({...formData, assigned_manager: e.target.value})}
                    className="w-full h-10 rounded-md border border-slate-200 bg-white/80 px-3 text-sm text-slate-900"
                    required
                    data-testid="project-pm-select"
                  >
                    <option value="">Pick a PM…</option>
                    {VISIBLE_PMS.map(pm => <option key={pm} value={pm}>{pm}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_person" className="text-slate-800">Customer Contact</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                    data-testid="project-contact-input"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_po_number" className="text-slate-800">Customer PO #</Label>
                  <Input
                    id="customer_po_number"
                    value={formData.customer_po_number}
                    onChange={(e) => setFormData({...formData, customer_po_number: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                    data-testid="project-cust-po-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date" className="text-slate-800">Start Date (Optional)</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                <div>
                  <Label htmlFor="end_date" className="text-slate-800">End Date (Optional)</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="quote_id" className="text-slate-800">Related Quote (Optional)</Label>
                <Select value={formData.quote_id} onValueChange={(value) => setFormData({...formData, quote_id: value})}>
                  <SelectTrigger className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900">
                    <SelectValue placeholder="Select a quote" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/60 backdrop-blur-xl border-slate-200">
                    <SelectItem value="none">No quote selected</SelectItem>
                    {quotes.map((quote) => (
                      <SelectItem key={quote.id} value={quote.id}>
                        {quote.quote_number} - {quote.project_title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description" className="text-slate-800">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  rows={3}
                  required
                />
              </div>

              <div className="flex justify-end space-x-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  Create Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Analytics — follows the year filter (This FY when 'all' / 'recent') */}
      <ProjectsAnalytics year={yearFilter === 'all' || yearFilter === 'recent' ? currentYearPrefix : yearFilter} />

      {/* Filters and Controls */}
      <div className="space-y-4 mb-6 mx-6 bg-white p-6 rounded-xl border border-slate-200 shadow-lg">
        {/* Search and Status Filter */}
        <div className="flex space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-600" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-300 text-slate-900 placeholder-slate-400 rounded-xl focus:ring-2 focus:ring-blue-500 font-medium"
            />
          </div>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger data-testid="project-year-filter" className="w-36 bg-white border-slate-300 text-slate-900 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              <SelectItem value="all">All Years</SelectItem>
              <SelectItem value="recent">20{prevYearPrefix} + 20{currentYearPrefix}</SelectItem>
              {availableYears.map((y) => (
                <SelectItem key={y} value={y}>20{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2" data-testid="status-toggles">
            {[
              { key: 'in_progress', label: 'In Progress', on: 'bg-orange-500 text-white border-orange-500', off: 'bg-white text-orange-600 border-orange-300 hover:bg-orange-50' },
              { key: 'invoiced', label: 'Sent', on: 'bg-blue-600 text-white border-blue-600', off: 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50' },
              { key: 'completed', label: 'Paid', on: 'bg-emerald-600 text-white border-emerald-600', off: 'bg-white text-emerald-600 border-emerald-300 hover:bg-emerald-50' },
            ].map(({ key, label, on, off }) => {
              const active = activeStatuses.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`status-toggle-${key}`}
                  onClick={() => setActiveStatuses(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key])}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${active ? on : off}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Project Manager Tabs */}
        <div className="flex flex-wrap gap-1 sm:gap-2 overflow-x-auto">
          {(() => {
            const uniqueManagers = ['all', ...new Set(projects.map(p => p.assigned_manager).filter(m => m && !hiddenPMs.includes(m)))];
            return uniqueManagers.map(manager => (
              <button
                key={manager}
                onClick={() => setProjectManagerFilter(manager)}
                className={`px-3 sm:px-6 py-1.5 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                  projectManagerFilter === manager
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                    : 'bg-white/80 text-slate-700 hover:bg-blue-50'
                }`}
              >
                {manager === 'all' ? 'All' : (manager || 'N/A')}
                {manager !== 'all' && (
                  <span className="ml-1 text-[10px] sm:text-xs">
                    {projects.filter(p => p.assigned_manager === manager).length}
                  </span>
                )}
              </button>
            ));
          })()}
        </div>

        {/* Advanced Filters and View Controls */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div className="flex flex-wrap gap-2">
            {/* Advanced Filters (Admin/Super Admin only) */}
            {canUseAdvancedFilters && (
              <>
                <Select value={projectManagerFilter} onValueChange={setProjectManagerFilter}>
                  <SelectTrigger className="w-full sm:w-48 bg-white/60 backdrop-blur-xl border-slate-200 text-slate-900 text-xs sm:text-sm">
                    <SelectValue placeholder="All PMs" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/60 backdrop-blur-xl border-slate-200">
                    <SelectItem value="all">All Project Managers</SelectItem>
                    {uniqueProjectManagers.map((manager) => (
                      <SelectItem key={manager} value={manager}>
                        {manager}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={customerFilter} onValueChange={setCustomerFilter}>
                  <SelectTrigger className="w-full sm:w-48 bg-white/60 backdrop-blur-xl border-slate-200 text-slate-900 text-xs sm:text-sm">
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/60 backdrop-blur-xl border-slate-200">
                    <SelectItem value="all">All Customers</SelectItem>
                    {uniqueCustomers.map((customer) => (
                      <SelectItem key={customer} value={customer}>
                        {customer}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-48 bg-white/60 backdrop-blur-xl border-slate-200 text-slate-900 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white/60 backdrop-blur-xl border-slate-200">
                    <SelectItem value="created_at">Date Created</SelectItem>
                    <SelectItem value="project_number">Project Number</SelectItem>
                    <SelectItem value="title">Project Title</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="pm_customer">PM + Customer</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                    <SelectItem value="estimated_budget">Budget</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="bg-white/60 backdrop-blur-xl border-slate-200 text-slate-900 hover:bg-white/80 backdrop-blur-sm"
                >
                  <SortAsc className={`h-4 w-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                </Button>
              </>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-slate-600 text-sm">View:</span>
            <div className="flex bg-white/60 backdrop-blur-xl rounded-lg p-1">
              <Button
                variant={viewMode === 'tiles' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('tiles')}
                className={`h-8 px-3 ${
                  viewMode === 'tiles' 
                    ? 'bg-blue-600 text-slate-900' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/80 backdrop-blur-sm'
                }`}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={`h-8 px-3 ${
                  viewMode === 'list' 
                    ? 'bg-blue-600 text-slate-900' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/80 backdrop-blur-sm'
                }`}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Clear Filters Button (for advanced users) */}
        {canUseAdvancedFilters && (projectManagerFilter !== 'all' || customerFilter !== 'all' || sortBy !== 'created_at' || activeStatuses.length > 0) && (
          <div className="flex justify-start">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setProjectManagerFilter('all');
                setCustomerFilter('all');
                setActiveStatuses([]);
                setSortBy('created_at');
                setSortOrder('desc');
              }}
              className="bg-white/60 backdrop-blur-xl border-slate-200 text-slate-600 hover:text-slate-900"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Projects Display */}
      {filteredProjects.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <Briefcase className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No projects found</h3>
            <p className="text-slate-600 mb-6">
              {projects.length === 0 ? 'Create your first project to get started' : 'Try adjusting your search or filters'}
            </p>
            {projects.length === 0 && (
              <Button onClick={() => setIsCreateOpen(true)} className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'tiles' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mx-6">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className="bg-white border-slate-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg cursor-pointer"
              onClick={() => navigate(`/projects/${project.project_number}`)}
            >
              <CardHeader className="border-b border-slate-200 pb-4 bg-white">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <CardTitle className="text-xl font-bold text-slate-900 mb-1">{project.title || 'Untitled Project'}</CardTitle>
                    <p className="text-sm font-medium text-blue-600">Project #{project.project_number}</p>
                  </div>
                  <span className={getStatusBadgeClass(project.status || 'planning')}>
                    {(project.status || 'planning').replace('_', ' ')}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>Progress</span>
                    <span>{project.status === 'completed' ? '100%' : project.status === 'in_progress' ? '45%' : '10%'}</span>
                  </div>
                  <div className="progress-bar h-2">
                    <div 
                      className={`progress-fill ${getStatusColor(project.status || 'planning')} h-full`}
                      style={{ 
                        width: project.status === 'completed' ? '100%' : 
                               project.status === 'in_progress' ? '45%' : '10%' 
                      }}
                    ></div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-600 text-sm flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      Client
                    </p>
                    <p className="text-slate-900 font-medium">{project.client_name || 'Unknown Client'}</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-600 text-sm flex items-center">
                      <DollarSign className="h-3 w-3 mr-1" />
                      Project Amount
                    </p>
                    <p className="text-green-700 font-bold">{formatCurrency(project.budget || project.estimated_budget || 0)}</p>
                  </div>

                  {/* Invoice Information */}
                  {project.invoices && project.invoices.length > 0 && (
                    <div>
                      <p className="text-slate-600 text-sm flex items-center">
                        <FileText className="h-3 w-3 mr-1" />
                        Invoices ({project.invoice_count || project.invoices.length})
                      </p>
                      <div className="text-slate-900 text-xs">
                        <p className="font-medium">Total: {formatCurrency(project.total_invoiced || project.estimated_budget || 0)}</p>
                        {project.invoices.length <= 2 ? (
                          // Show all invoices if 2 or fewer
                          project.invoices.map((invoice, idx) => (
                            <p key={idx} className="text-slate-700">
                              {invoice.invoice_number}: {formatCurrency(invoice.amount || 0)}
                            </p>
                          ))
                        ) : (
                          // Show summary if more than 2 invoices
                          <>
                            {project.invoices.slice(0, 2).map((invoice, idx) => (
                              <p key={idx} className="text-slate-700">
                                {invoice.invoice_number}: {formatCurrency(invoice.amount || 0)}
                              </p>
                            ))}
                            <p className="text-slate-600 italic">
                              +{project.invoices.length - 2} more invoice{project.invoices.length - 2 > 1 ? 's' : ''}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-slate-600 text-sm flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      Manager
                    </p>
                    <p className="text-slate-900">{project.assigned_manager || 'Unassigned'}</p>
                  </div>

                  {project.start_date && (
                    <div>
                      <p className="text-slate-600 text-sm flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        Start Date
                      </p>
                      <p className="text-slate-900">{formatDate(project.start_date)}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                    <Select 
                      value={project.status || 'planning'} 
                      onValueChange={(value) => handleStatusUpdate(project.id, value)}
                    >
                      <SelectTrigger className="w-28 h-8 text-xs bg-white border-slate-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-300">
                        <SelectItem value="planning">Planning</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleWIP(project.id);
                        }}
                        className={`h-8 px-2 text-xs font-semibold ${project.is_wip ? 'text-green-700 hover:text-green-800 hover:bg-green-100' : 'text-slate-700 hover:text-slate-900 hover:bg-white/80 backdrop-blur-sm'}`}
                        title={project.is_wip ? 'Mark as Completed' : 'Mark as WIP'}
                      >
                        {project.is_wip ? 'WIP' : 'Done'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(project);
                        }}
                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.id, project.title);
                        }}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-100"
                        title="Delete Project"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        // List View
        <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200 overflow-hidden mx-4 sm:mx-6">
          {/* Table Header - desktop only */}
          <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-white/80 backdrop-blur-sm text-slate-700 text-sm font-medium border-b border-slate-200">
            <div className="col-span-2">Project #</div>
            <div className="col-span-2">Title</div>
            <div className="col-span-2">Customer</div>
            <div className="col-span-1">Manager</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Project Amount</div>
            <div className="col-span-1">Progress</div>
            <div className="col-span-1">Start Date</div>
            <div className="col-span-1">Actions</div>
          </div>
          
          {/* Table Body */}
          <div className="divide-y divide-slate-200">
            {filteredProjects.map((project) => (
              <div 
                key={project.id} 
                className="md:grid md:grid-cols-12 md:gap-4 p-4 hover:bg-white/80 backdrop-blur-sm cursor-pointer transition-colors"
                onClick={() => navigate(`/projects/${project.project_number}`)}
              >
                {/* Mobile: card layout */}
                <div className="md:hidden space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">{project.project_number}</span>
                    <span className={`${getStatusBadgeClass(project.status)} text-xs`}>{project.status?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-sm text-slate-900 font-medium">{project.client_name}</p>
                  <p className="text-xs text-slate-600">{project.title}</p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{project.assigned_manager}</span>
                    {project.total_value > 0 && <span className="text-green-700 font-medium">{formatCurrency(project.total_value)}</span>}
                  </div>
                </div>

                {/* Desktop: grid layout */}
                <div className="hidden md:block col-span-2">
                  <div className="text-slate-900 font-medium">{project.project_number}</div>
                  {project.invoices && project.invoices.length > 0 && (
                    <div className="text-xs text-slate-600">
                      {project.invoices.length} invoice{project.invoices.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* Title */}
                <div className="hidden md:block col-span-2">
                  <div className="text-slate-900 truncate" title={project.title}>
                    {project.title}
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {project.description}
                  </div>
                </div>

                {/* Customer */}
                <div className="hidden md:block col-span-2">
                  <div className="text-slate-900 truncate" title={project.client_name}>
                    {project.client_name}
                  </div>
                  {project.invoices && project.invoices.length > 0 && (
                    <div className="text-xs text-green-700">
                      {formatCurrency(project.total_invoiced || project.estimated_budget)}
                    </div>
                  )}
                </div>

                {/* Manager */}
                <div className="hidden md:block col-span-1">
                  <div className="text-slate-900 text-sm truncate" title={project.assigned_manager}>
                    {project.assigned_manager}
                  </div>
                </div>

                {/* Status */}
                <div className="hidden md:block col-span-1">
                  <span className={`${getStatusBadgeClass(project.status)} text-xs`}>
                    {project.status.replace('_', ' ')}
                  </span>
                </div>

                {/* Project Amount */}
                <div className="hidden md:block col-span-1">
                  <div className="text-green-700 font-bold text-sm">
                    {formatCurrency(project.budget || project.estimated_budget)}
                  </div>
                  {project.actual_cost > 0 && (
                    <div className="text-xs text-slate-600">
                      Actual: {formatCurrency(project.actual_cost)}
                    </div>
                  )}
                </div>

                {/* Progress */}
                <div className="hidden md:block col-span-1">
                  <div className="text-xs text-slate-600 mb-1">
                    {project.status === 'completed' ? '100%' : project.status === 'in_progress' ? '45%' : '10%'}
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getStatusColor(project.status)}`}
                      style={{ 
                        width: project.status === 'completed' ? '100%' : 
                               project.status === 'in_progress' ? '45%' : '10%' 
                      }}
                    ></div>
                  </div>
                </div>

                {/* Start Date */}
                <div className="hidden md:block col-span-1">
                  <div className="text-slate-900 text-sm">
                    {project.start_date ? formatDate(project.start_date) : 'TBD'}
                  </div>
                  {project.end_date && (
                    <div className="text-xs text-slate-600">
                      End: {formatDate(project.end_date)}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="hidden md:block col-span-1" onClick={(e) => e.stopPropagation()}>
                  <div className="flex space-x-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleWIP(project.id);
                      }}
                      className={`h-7 px-2 text-xs font-semibold ${project.is_wip ? 'text-green-700 hover:text-green-800 hover:bg-green-100' : 'text-slate-700 hover:text-slate-900 hover:bg-white/80'}`}
                      title={project.is_wip ? 'Mark as Completed' : 'Mark as WIP'}
                    >
                      {project.is_wip ? 'WIP' : 'Done'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(project);
                      }}
                      className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(project.id, project.title);
                      }}
                      className="h-7 w-7 p-0 text-red-600 hover:text-red-800 hover:bg-red-100"
                      title="Delete Project"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-white/60 backdrop-blur-xl border-slate-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Project</DialogTitle>
            <DialogDescription className="text-slate-600">
              Update project information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditProject} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_title" className="text-slate-800">Project Title</Label>
                <Input
                  id="edit_title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_client_name" className="text-slate-800">Client Name</Label>
                <Input
                  id="edit_client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_estimated_budget" className="text-slate-800">Estimated Budget</Label>
                <Input
                  id="edit_estimated_budget"
                  type="number"
                  step="0.01"
                  value={formData.estimated_budget}
                  onChange={(e) => setFormData({...formData, estimated_budget: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_assigned_manager" className="text-slate-800">Assigned Manager</Label>
                <Input
                  id="edit_assigned_manager"
                  value={formData.assigned_manager}
                  onChange={(e) => setFormData({...formData, assigned_manager: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_start_date" className="text-slate-800">Start Date (Optional)</Label>
                <Input
                  id="edit_start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                />
              </div>
              <div>
                <Label htmlFor="edit_end_date" className="text-slate-800">End Date (Optional)</Label>
                <Input
                  id="edit_end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_quote_id" className="text-slate-800">Related Quote (Optional)</Label>
              <Select value={formData.quote_id} onValueChange={(value) => setFormData({...formData, quote_id: value})}>
                <SelectTrigger className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900">
                  <SelectValue placeholder="Select a quote" />
                </SelectTrigger>
                <SelectContent className="bg-white/60 backdrop-blur-xl border-slate-200">
                  <SelectItem value="none">No quote selected</SelectItem>
                  {quotes.map((quote) => (
                    <SelectItem key={quote.id} value={quote.id}>
                      {quote.quote_number} - {quote.project_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_description" className="text-slate-800">Description</Label>
              <Textarea
                id="edit_description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                rows={3}
                required
              />
            </div>

            {/* Site Information Section for SMS Dispatch System */}
            <div className="border-t border-slate-200 pt-4 mt-6">
              <h3 className="text-slate-800 font-medium mb-4 flex items-center gap-2">
                <span>📱</span> Site Information (for SMS Dispatch) 
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_site_name" className="text-slate-800">Site Name</Label>
                  <Input
                    id="edit_site_name"
                    placeholder="e.g., Johnson Manufacturing Plant"
                    value={formData.site_name}
                    onChange={(e) => setFormData({...formData, site_name: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_site_contact_name" className="text-slate-800">Site Contact</Label>
                  <Input
                    id="edit_site_contact_name"
                    placeholder="e.g., Mike Johnson - Site Supervisor"
                    value={formData.site_contact_name}
                    onChange={(e) => setFormData({...formData, site_contact_name: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <Label htmlFor="edit_site_address" className="text-slate-800">Site Address</Label>
                <Input
                  id="edit_site_address"
                  placeholder="e.g., 123 Industrial Blvd, Toronto ON M1B 2C3"
                  value={formData.site_address}
                  onChange={(e) => setFormData({...formData, site_address: e.target.value})}
                  className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="edit_site_contact_phone" className="text-slate-800">Contact Phone</Label>
                  <Input
                    id="edit_site_contact_phone"
                    placeholder="e.g., +14165551234"
                    value={formData.site_contact_phone}
                    onChange={(e) => setFormData({...formData, site_contact_phone: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_site_contact_email" className="text-slate-800">Contact Email</Label>
                  <Input
                    id="edit_site_contact_email"
                    type="email"
                    placeholder="e.g., mike.johnson@client.com"
                    value={formData.site_contact_email}
                    onChange={(e) => setFormData({...formData, site_contact_email: e.target.value})}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Update Project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-white/90 backdrop-blur-xl border-red-300 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-800 text-xl flex items-center gap-2">
              <span className="text-2xl">⚠️</span> Delete Project
            </DialogTitle>
            <DialogDescription className="text-slate-700 pt-4">
              Are you sure you want to permanently delete this project?
            </DialogDescription>
          </DialogHeader>
          
          {projectToDelete && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-slate-900 font-semibold text-lg mb-1">
                "{projectToDelete.title}"
              </p>
              <p className="text-red-700 text-sm font-medium">
                This action cannot be undone!
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={cancelDelete}
              className="bg-white/80 hover:bg-white text-slate-700 border-slate-300"
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Project
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;